import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { cn } from "~/lib/utils";
import { ToolCallItem } from "./ToolCallItem";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallsDisclosureProps {
  entries: ToolCallEntry[];
  // Whether the owning turn is still streaming. Toggling the disclosure while
  // a run is in flight is treated as a durable preference (see below); on a
  // finished turn it's a local-only convenience.
  isRunning: boolean;
}

// Tool-call list for a turn: a top-level disclosure wrapping a vertical list
// of individually-collapsible tool calls. Open while the turn is running (the
// user's mid-run toggle is remembered in `collapseToolDisclosureDuringRun` so
// the next run honors their choice); collapses when the turn finishes, so a
// settled turn — live or reloaded from history — tucks its tool calls away.
export const ToolCallsDisclosure = ({ entries, isRunning }: ToolCallsDisclosureProps) => {
  const [prefs, updatePreferences] = useUserPreferences();
  const [open, setOpen] = useState(isRunning ? !prefs.collapseToolDisclosureDuringRun : false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // A toggle during a run is the user telling us how they want disclosures
    // to behave while tools stream — persist it. `next === false` (closed)
    // means "collapse during runs"; opening clears the preference.
    if (isRunning) updatePreferences({ collapseToolDisclosureDuringRun: !next });
  };

  // Per-item open state. While a run is in flight we keep the latest call
  // expanded and, as new calls stream in, collapse the previously-latest one
  // and expand the arrival — focusing the list on what's happening now while
  // leaving any item the user opened themselves untouched. A finished turn
  // starts fully collapsed.
  const lastId = entries[entries.length - 1]?.id;
  const [openIds, setOpenIds] = useState<Set<string>>(() =>
    isRunning && lastId ? new Set([lastId]) : new Set()
  );
  const prevLastIdRef = useRef(lastId);

  useEffect(() => {
    if (!isRunning || !lastId || lastId === prevLastIdRef.current) return;
    const prevLast = prevLastIdRef.current;
    prevLastIdRef.current = lastId;
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (prevLast) next.delete(prevLast);
      next.add(lastId);
      return next;
    });
  }, [isRunning, lastId]);

  // Drive the disclosure off the run lifecycle. On settle, collapse the whole
  // disclosure and reset the inner cards. On (re)start — the same turn can
  // resume in place after answering a deferred HITL tool, without remounting —
  // reopen (honoring the collapse preference) and re-focus the latest call so
  // live progress isn't stuck hidden behind a collapsed disclosure.
  const wasRunningRef = useRef(isRunning);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      setOpen(false);
      setOpenIds(new Set());
    } else if (!wasRunningRef.current && isRunning) {
      setOpen(!prefs.collapseToolDisclosureDuringRun);
      if (lastId) setOpenIds(new Set([lastId]));
    }
    wasRunningRef.current = isRunning;
  }, [isRunning, lastId, prefs.collapseToolDisclosureDuringRun]);

  const setItemOpen = (id: string, itemOpen: boolean) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (itemOpen) next.add(id);
      else next.delete(id);
      return next;
    });

  if (entries.length === 0) return null;

  const summary = entries.length === 1 ? "1 tool call" : `${entries.length} tool calls`;

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange} className="w-full">
      <CollapsibleTrigger
        className={cn(
          "group inline-flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        <span>{summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent animated>
        <div className="mt-2 flex flex-col gap-2">
          {entries.map((entry) => (
            <ToolCallItem
              key={entry.id}
              entry={entry}
              open={openIds.has(entry.id)}
              onOpenChange={(itemOpen) => setItemOpen(entry.id, itemOpen)}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
